import {
    collection,
    collectionGroup,
    getDocs,
    query,
    where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
// Extensie is verplicht: package.json staat op "type": "module", dus .js-bestanden
// worden als strikte ESM behandeld.
import { db, functions } from '../firebase.js';

/**
 * Data-toegang voor de gebruikersgeschiedenis over seizoenen heen.
 *
 * De history staat per gebruiker in `users/{uid}/history/{seasonId}` en wordt gevuld
 * door de callable `captureSeasonUserStats`. Elk document heeft `position` (eindstand
 * finaleronde, 1-based of null), `weekPrizes` (gewonnen poule-speelrondes), `finalist`
 * (of de finaleronde gehaald is), `seasonName` en `userName`. Goud/zilver/brons leiden
 * we hier af uit `position`.
 */

/** Of de gebruiker dat seizoen de finaleronde haalde. Oude docs missen `finalist`. */
function isFinalist(data) {
    return data.finalist ?? (data.position != null);
}

/** Legt de statistieken van één seizoen vast via de Cloud Function. */
export async function captureSeasonUserStats(seasonId) {
    const fn = httpsCallable(functions, 'captureSeasonUserStats');
    const result = await fn({ seasonId: String(seasonId) });
    return result.data;
}

/** Vertaalt een eindpositie naar een bekerkleur, of null als er geen podiumplek is. */
export function medalForPosition(position) {
    if (position === 1) return 'gold';
    if (position === 2) return 'silver';
    if (position === 3) return 'bronze';
    return null;
}

/**
 * De aller-tijden-ranglijst: per gebruiker geaggregeerd over alle seizoenen. Gesorteerd
 * op meeste goud, dan zilver, dan brons, dan medailles (weekprijzen), dan finaleplekken.
 *
 * @returns {Promise<Array<{uid, name, gold, silver, bronze, medals, finales, seasons}>>}
 */
export async function fetchAllTimeRankings() {
    const snapshot = await getDocs(collectionGroup(db, 'history'));

    const byUid = new Map();
    snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const uid = docSnap.ref.parent.parent?.id;
        if (!uid) return;

        if (!byUid.has(uid)) {
            byUid.set(uid, { uid, name: data.userName || 'Onbekend', gold: 0, silver: 0, bronze: 0, medals: 0, finales: 0, seasons: 0 });
        }
        const entry = byUid.get(uid);
        if (data.userName) entry.name = data.userName;
        if (data.position === 1) entry.gold += 1;
        else if (data.position === 2) entry.silver += 1;
        else if (data.position === 3) entry.bronze += 1;
        entry.medals += data.weekPrizes || 0;
        if (isFinalist(data)) entry.finales += 1;
        entry.seasons += 1;
    });

    return Array.from(byUid.values()).sort((a, b) => (
        b.gold - a.gold
        || b.silver - a.silver
        || b.bronze - a.bronze
        || b.medals - a.medals
        || b.finales - a.finales
        || a.name.localeCompare(b.name)
    ));
}

/**
 * De prijzenkast van één gebruiker: alle seizoensresultaten, nieuwste eerst.
 *
 * @returns {Promise<{seasons: Array, totals: {gold, silver, bronze, medals, finales}}>}
 */
export async function fetchUserTrophies(uid) {
    const snapshot = await getDocs(collection(db, 'users', String(uid), 'history'));

    const seasons = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data(), finalist: isFinalist(d.data()) }))
        .sort((a, b) => String(b.seasonId).localeCompare(String(a.seasonId)));

    const totals = { gold: 0, silver: 0, bronze: 0, medals: 0, finales: 0 };
    seasons.forEach((s) => {
        const medal = medalForPosition(s.position);
        if (medal) totals[medal] += 1;
        totals.medals += s.weekPrizes || 0;
        if (s.finalist) totals.finales += 1;
    });

    return { seasons, totals };
}

/**
 * De uids van alle gebruikers die ooit een seizoen wonnen (positie 1). Bron voor de
 * goud-beker naast oud-kampioenen in de gewone (subleague-)ranglijst.
 *
 * @returns {Promise<Set<string>>}
 */
export async function fetchGoldMedalUids() {
    const snapshot = await getDocs(query(collectionGroup(db, 'history'), where('position', '==', 1)));
    const uids = new Set();
    snapshot.docs.forEach((docSnap) => {
        const uid = docSnap.ref.parent.parent?.id;
        if (uid) uids.add(uid);
    });
    return uids;
}
